import apiClient from '../../shared/services/apiClient';

/**
 * System Service (Admin Only)
 *
 * Handles admin-level system management operations:
 * system statistics, health monitoring, dynamic settings and rate-limit rules.
 *
 * Routes live under /api/admin/system/*, /api/admin/settings/* and
 * /api/admin/rate-limit/*.
 */
export const systemService = {
    // 获取系统统计信息
    getSystemStats: async () => {
        try {
            const response = await apiClient.get('/api/admin/system/stats');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // 获取系统健康状态（CPU/内存/磁盘/运行时长）
    getHealth: async () => {
        try {
            const response = await apiClient.get('/api/admin/system/health');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 系统设置（动态、DB 驱动，按分类分组）
    // ==========================================

    // 获取系统设置（按分类分组返回；可选 category 过滤）
    getSettings: async (params = {}) => {
        try {
            const response = await apiClient.get('/api/admin/settings', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // 获取所有设置分类
    getCategories: async () => {
        try {
            const response = await apiClient.get('/api/admin/settings/categories');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // 只读：.env 控制的运行开关当前生效值
    getRuntimeSettings: async () => {
        try {
            const response = await apiClient.get('/api/admin/settings/runtime');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // 批量更新系统设置（settings 为 key=>value 映射）
    updateSettings: async (settings) => {
        try {
            const response = await apiClient.post('/api/admin/settings/update', { settings });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // 新增一个设置项
    createSetting: async (payload) => {
        try {
            const response = await apiClient.post('/api/admin/settings/create', payload);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // 删除一个设置项（按 key）
    deleteSetting: async (key) => {
        try {
            const response = await apiClient.delete('/api/admin/settings/delete', {
                params: { key }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 速率限制规则（/api/admin/rate-limit/*）
    // ==========================================

    getRateLimitRules: async () => {
        try {
            const response = await apiClient.get('/api/admin/rate-limit');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    getRateLimitStats: async () => {
        try {
            const response = await apiClient.get('/api/admin/rate-limit/stats');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    getRateLimitBlocked: async () => {
        try {
            const response = await apiClient.get('/api/admin/rate-limit/blocked');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    toggleRateLimit: async (enabled) => {
        try {
            const response = await apiClient.post('/api/admin/rate-limit/toggle', { enabled });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    saveRateLimitRule: async (rule) => {
        try {
            const response = await apiClient.post('/api/admin/rate-limit/save', rule);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    deleteRateLimitRule: async (scenario) => {
        try {
            const response = await apiClient.delete('/api/admin/rate-limit/delete', {
                params: { scenario }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    unblockRateLimit: async (key) => {
        try {
            const response = await apiClient.post('/api/admin/rate-limit/unblock', { key });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    clearRateLimitCache: async () => {
        try {
            const response = await apiClient.post('/api/admin/rate-limit/clear');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    initRateLimitRules: async () => {
        try {
            const response = await apiClient.post('/api/admin/rate-limit/init');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    exportRateLimitRules: async () => {
        try {
            const response = await apiClient.get('/api/admin/rate-limit/export', {
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    importRateLimitRules: async (jsonString) => {
        try {
            const response = await apiClient.post('/api/admin/rate-limit/import', jsonString, {
                headers: { 'Content-Type': 'application/json' }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }
};

export default systemService;
